import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, Select, Table, Tag, Col, DatePicker } from 'antd';
import StoreSelect from '../../../components/common/StoreSelect';
import { useTranslation } from 'react-i18next';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';
import dayjs from 'dayjs';
import FilterPanel from '../../../components/common/FilterPanel';
import EllipsisCell from '@/components/common/EllipsisCell';
import type { WaveConfigLogDTO, QueryWaveConfigLogPageRequest } from '../../../types/wave/waveConfigLog';
import type { Paging } from '../../../types/common';
import * as waveConfigLogService from '../../../services/wave/waveConfigLogService';
import { WaveRuleStatusOptions } from '../../../constants/wave';

const WaveConfigLogList: React.FC = () => {
  const { t } = useTranslation();
  const { tableWrapRef, scrollY } = useTableScrollHeight();
  const [searchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<WaveConfigLogDTO[]>([]);
  const [pagination, setPagination] = useState<Paging>({ currentPage: 1, pageSize: 10, totalCount: 0 });

  // Load data
  const loadData = async (params: QueryWaveConfigLogPageRequest) => {
    setLoading(true);
    try {
      const response = await waveConfigLogService.pageWaveConfigLogs(params);
      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setPagination(response.data.paging);
      }
    } catch (error) {
      // Error handled by service
    } finally {
      setLoading(false);
    }
  };

  // Initialize
  useEffect(() => {
    loadData({ currentPage: 1, pageSize: 10 });
  }, []);

  // Search
  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    const params: QueryWaveConfigLogPageRequest = {
      currentPage: 1,
      pageSize: pagination.pageSize,
      storeCode: values.storeCode,
      ruleCode: values.ruleCode,
      status: values.status,
      gmtCreateStart: values.gmtCreateRange?.[0]?.startOf('day').valueOf(),
      gmtCreateEnd: values.gmtCreateRange?.[1]?.endOf('day').valueOf(),
    };
    loadData(params);
  };

  // Reset
  const handleReset = () => {
    searchForm.resetFields();
    loadData({ currentPage: 1, pageSize: pagination.pageSize });
  };

  // Pagination change
  const handlePageChange = (page: number, pageSize: number) => {
    const values = searchForm.getFieldsValue();
    const params: QueryWaveConfigLogPageRequest = {
      currentPage: page,
      pageSize,
      storeCode: values.storeCode,
      ruleCode: values.ruleCode,
      status: values.status,
      gmtCreateStart: values.gmtCreateRange?.[0]?.startOf('day').valueOf(),
      gmtCreateEnd: values.gmtCreateRange?.[1]?.endOf('day').valueOf(),
    };
    loadData(params);
  };

  // Format date
  const formatDate = (timestamp: number | null) => {
    return timestamp ? dayjs(timestamp).format('DD-MM-YYYY') : '-';
  };

  // Format datetime
  const formatDateTime = (timestamp: number | null) => {
    return timestamp ? dayjs(timestamp).format('DD-MM-YYYY HH:mm:ss') : '-';
  };

  // Render status tag
  const renderStatusTag = (status: string) => {
    const option = WaveRuleStatusOptions.find(opt => opt.value === status);
    if (!option) return status;
    return <Tag color={option.color}>{t(option.label)}</Tag>;
  };

  // Cache current date for performance optimization
  const currentDate = useMemo(() => dayjs(), []);

  // Render is expired (compare effectiveEndDate with current date)
  const renderIsExpired = (effectiveEndDate: number | null) => {
    const expired = !!effectiveEndDate && currentDate.isAfter(dayjs(effectiveEndDate));
    return expired ? <Tag color="red">{t('common.yes')}</Tag> : <Tag color="green">{t('common.no')}</Tag>;
  };

  // Render picking mode
  const renderPickingMode = (mode: string) => {
    if (mode === 'MANUAL_PICKING') {
      return t('wave.waveRule.form.fields.manualPicking');
    } else if (mode === 'ORDER_PICKING') {
      return t('wave.waveRule.form.fields.orderPicking');
    } else if (mode === 'BATCH_PICKING') {
      return t('wave.waveRule.form.fields.batchPicking');
    }
    return mode || '-';
  };

  // Table columns
  const columns = [
    {
      title: t('wave.waveConfigLog.list.table.storeCode'),
      dataIndex: 'storeCode',
      key: 'storeCode',
      width: 130,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>
    },
    {
      title: t('wave.waveConfigLog.list.table.storeName'),
      dataIndex: 'storeName',
      key: 'storeName',
      width: 200,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>
    },
    {
      title: t('wave.waveConfigLog.list.table.ruleCode'),
      dataIndex: 'ruleCode',
      key: 'ruleCode',
      width: 140,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>
    },
    {
      title: t('wave.waveConfigLog.list.table.ruleName'),
      dataIndex: 'ruleName',
      key: 'ruleName',
      width: 180,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>
    },
    {
      title: t('wave.waveConfigLog.list.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: renderStatusTag,
    },
    {
      title: t('wave.waveConfigLog.list.table.isExpired'),
      dataIndex: 'effectiveEndDate',
      key: 'isExpired',
      width: 100,
      render: (value: number | null) => renderIsExpired(value),
    },
    {
      title: t('wave.waveConfigLog.list.table.gmtCreate'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: (timestamp: number | null) => <EllipsisCell>{formatDateTime(timestamp)}</EllipsisCell>
    },
    {
      title: t('wave.waveConfigLog.list.table.executionMode'),
      dataIndex: 'executionMode',
      key: 'executionMode',
      width: 150,
      render: (mode: string) => <EllipsisCell>{renderPickingMode(mode)}</EllipsisCell>
    },
    {
      title: t('wave.waveConfigLog.list.table.effectiveStartDate'),
      dataIndex: 'effectiveStartDate',
      key: 'effectiveStartDate',
      width: 160,
      render: (timestamp: number | null) => <EllipsisCell>{formatDate(timestamp)}</EllipsisCell>
    },
    {
      title: t('wave.waveConfigLog.list.table.effectiveEndDate'),
      dataIndex: 'effectiveEndDate',
      key: 'effectiveEndDate',
      width: 170,
      render: (value: number | null) => <EllipsisCell>{value ? formatDate(value) : t('common.permanent')}</EllipsisCell>
    },
  ];

  // Search form fields
  const renderSearchFields = () => (
    <>
      <Col span={5}>
        <Form.Item name="storeCode" label={t('wave.waveConfigLog.list.search.storeCode')}>
          <StoreSelect placeholder={t('wave.waveConfigLog.list.search.placeholder.storeCode')} allowClear />
        </Form.Item>
      </Col>
      <Col span={5}>
        <Form.Item name="ruleCode" label={t('wave.waveConfigLog.list.search.ruleCode')}>
          <Input
            placeholder={t('wave.waveConfigLog.list.search.placeholder.ruleCode')}
            allowClear
          />
        </Form.Item>
      </Col>
      <Col span={5}>
        <Form.Item name="status" label={t('wave.waveConfigLog.list.search.status')}>
          <Select
            placeholder={t('wave.waveConfigLog.list.search.placeholder.status')}
            allowClear
            options={WaveRuleStatusOptions.map(opt => ({
              label: t(opt.label),
              value: opt.value,
            }))}
          />
        </Form.Item>
      </Col>
      <Col span={6} className="filter-col-date-range">
        <Form.Item name="gmtCreateRange" label={t('wave.waveConfigLog.list.search.gmtCreateRange')}>
          <DatePicker.RangePicker
            format="DD-MM-YYYY"
            placeholder={[
              t('wave.waveConfigLog.list.search.placeholder.startDate'),
              t('wave.waveConfigLog.list.search.placeholder.endDate'),
            ]}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Col>
    </>
  );

  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Search Panel */}
      <FilterPanel
        form={searchForm}
        onFilter={handleSearch}
        onReset={handleReset}
        loading={loading}
      >
        {renderSearchFields()}
      </FilterPanel>

      {/* Table */}
      <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          scroll={{ x: 1570, y: scrollY }}
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.pageSize,
            total: pagination.totalCount,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => t('common.pagination.total', { total }),
            onChange: handlePageChange,
          }}
        />
      </div>
    </div>
  );
};

export default WaveConfigLogList;
